"use client";
import { useEffect, useRef, useState } from "react";
import { cn } from "@newxsystems/shared";
import { Icon } from "@templates/design-system";
import { getComponent } from "../../../../client";
import { WohaAction } from "../WohaAction/WohaAction";
import { WohaMedia } from "../WohaMedia/WohaMedia";
import type { IMegaMenuNavigationProps } from "./MegaMenuNavigation.props";

export const MegaMenuNavigation = ({ actions, items, logo, sticky }: IMegaMenuNavigationProps) => {
  const Link = getComponent("Link");
  const [openDesktop, setOpenDesktop] = useState<number | null>(null);
  const [openMobile, setOpenMobile] = useState<number | null>(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const headerRef = useRef<HTMLElement>(null);
  const dialogRef = useRef<HTMLDialogElement>(null);
  useEffect(() => {
    const close = (event: MouseEvent) => {
      if (!headerRef.current?.contains(event.target as Node)) setOpenDesktop(null);
    };
    const escape = (event: KeyboardEvent) => {
      if (event.key === "Escape") setOpenDesktop(null);
    };
    document.addEventListener("mousedown", close);
    document.addEventListener("keydown", escape);
    return () => {
      document.removeEventListener("mousedown", close);
      document.removeEventListener("keydown", escape);
    };
  }, []);
  useEffect(() => {
    const dialog = dialogRef.current;
    if (!dialog) return;
    if (drawerOpen && !dialog.open) dialog.showModal();
    if (!drawerOpen && dialog.open) dialog.close();
  }, [drawerOpen]);
  const navigate = () => {
    setDrawerOpen(false);
    setOpenMobile(null);
  };
  const linkClass =
    "inline-flex min-h-11 items-center rounded-lg px-3 text-sm font-medium text-charcoal transition-colors hover:bg-paper-mist focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-electric-blue";
  const nestedLink = (
    link: {
      accessibilityLabel?: string | null;
      href: string;
      label: string;
      openInNewTab?: boolean | null;
    },
    key: number,
    mobile = false,
  ) => (
    <Link
      aria-label={link.accessibilityLabel || undefined}
      className={cn(linkClass, "w-full")}
      href={link.href}
      key={key}
      onClick={mobile ? navigate : undefined}
      rel={link.openInNewTab ? "noopener noreferrer" : undefined}
      target={link.openInNewTab ? "_blank" : undefined}
    >
      {link.label}
    </Link>
  );
  if (!logo && !items?.length && !actions?.length) return null;
  return (
    <header
      className={cn(
        "z-40 border-b border-ash bg-white/95 backdrop-blur-md",
        sticky && "sticky top-0",
      )}
      ref={headerRef}
    >
      <div className="mx-auto flex min-h-20 w-full max-w-[80rem] items-center gap-6 px-5 sm:px-8 lg:px-12">
        {logo ? (
          <Link
            aria-label="Home"
            className="shrink-0 rounded-lg focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-electric-blue"
            href="/"
          >
            <WohaMedia
              className="h-10 w-44 rounded-none bg-transparent"
              imageClassName="object-contain object-left"
              media={logo}
              sizes="176px"
            />
          </Link>
        ) : null}
        <nav aria-label="Primary" className="ml-10 hidden items-center gap-1 lg:flex">
          {items?.map((item, index) =>
            item.blockType === "megaMenuDirectLink" ? (
              nestedLink(item.link, index)
            ) : (
              <div className="relative" key={index}>
                <button
                  aria-expanded={openDesktop === index}
                  className={cn(linkClass, "cursor-pointer gap-1")}
                  onClick={() => setOpenDesktop(openDesktop === index ? null : index)}
                  type="button"
                >
                  {item.label}
                  <Icon
                    className={cn(
                      "size-4 transition-transform",
                      openDesktop === index && "rotate-180",
                    )}
                    name="chevron-down"
                  />
                </button>
                {openDesktop === index ? (
                  <div
                    className={cn(
                      "absolute left-1/2 top-full mt-2 min-w-64 -translate-x-1/2 rounded-xl border border-ash bg-white p-2 shadow-md",
                      item.blockType === "megaMenuGroup" &&
                        "grid min-w-[36rem] grid-cols-2 gap-4 p-4",
                    )}
                  >
                    {item.blockType === "megaMenuDropdown"
                      ? item.links.map((link, linkIndex) => nestedLink(link, linkIndex))
                      : item.groupItems.map((group, groupIndex) => (
                          <div key={groupIndex}>
                            <p className="px-3 py-2 text-sm font-semibold text-charcoal">
                              {group.header}
                            </p>
                            {group.links.map((link, linkIndex) => nestedLink(link, linkIndex))}
                          </div>
                        ))}
                  </div>
                ) : null}
              </div>
            ),
          )}
        </nav>
        {actions?.length ? (
          <div className="ml-auto hidden gap-2 lg:flex">
            {actions.map((action, index) => (
              <WohaAction action={action} key={index} />
            ))}
          </div>
        ) : null}
        <button
          aria-label="Open navigation menu"
          className="ml-auto inline-flex size-11 items-center justify-center rounded-lg border border-ash text-charcoal focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-electric-blue lg:hidden"
          onClick={() => setDrawerOpen(true)}
          type="button"
        >
          <Icon name="menu" />
        </button>
      </div>
      <dialog
        className="m-0 h-dvh max-h-none w-full max-w-none bg-black/25 p-0 backdrop:bg-black/25 md:ml-auto md:w-[28rem]"
        onClose={() => setDrawerOpen(false)}
        ref={dialogRef}
      >
        <div className="ml-auto flex h-full w-full flex-col bg-white p-5 md:w-[28rem]">
          <div className="flex items-center justify-between border-b border-ash pb-4">
            {logo ? (
              <Link
                aria-label="Home"
                className="rounded-lg focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-electric-blue"
                href="/"
                onClick={navigate}
              >
                <WohaMedia
                  className="h-9 w-40 rounded-none bg-transparent"
                  imageClassName="object-contain object-left"
                  media={logo}
                  sizes="160px"
                />
              </Link>
            ) : (
              <span className="text-sm font-semibold text-charcoal">Menu</span>
            )}
            <button
              aria-label="Close navigation menu"
              className="inline-flex size-11 items-center justify-center rounded-lg hover:bg-paper-mist focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-electric-blue"
              onClick={() => setDrawerOpen(false)}
              type="button"
            >
              <Icon name="x" />
            </button>
          </div>
          <nav aria-label="Mobile primary" className="flex-1 overflow-y-auto py-4">
            {items?.map((item, index) =>
              item.blockType === "megaMenuDirectLink" ? (
                nestedLink(item.link, index, true)
              ) : (
                <div className="border-b border-ash" key={index}>
                  <button
                    aria-expanded={openMobile === index}
                    className={cn(linkClass, "w-full cursor-pointer justify-between")}
                    onClick={() => setOpenMobile(openMobile === index ? null : index)}
                    type="button"
                  >
                    {item.label}
                    <Icon
                      className={cn(
                        "size-4 transition-transform",
                        openMobile === index && "rotate-180",
                      )}
                      name="chevron-down"
                    />
                  </button>
                  {openMobile === index ? (
                    <div className="pb-3 pl-3">
                      {item.blockType === "megaMenuDropdown"
                        ? item.links.map((link, linkIndex) => nestedLink(link, linkIndex, true))
                        : item.groupItems.map((group, groupIndex) => (
                            <div key={groupIndex}>
                              <p className="px-3 py-2 text-sm font-semibold text-charcoal">
                                {group.header}
                              </p>
                              {group.links.map((link, linkIndex) =>
                                nestedLink(link, linkIndex, true),
                              )}
                            </div>
                          ))}
                    </div>
                  ) : null}
                </div>
              ),
            )}
          </nav>
          {actions?.length ? (
            <div className="flex flex-col gap-2 border-t border-ash pt-4">
              {actions.map((action, index) => (
                <WohaAction action={action} key={index} />
              ))}
            </div>
          ) : null}
        </div>
      </dialog>
    </header>
  );
};
MegaMenuNavigation.displayName = "Content::MegaMenuNavigation";
